import { get } from 'lodash';
import DatadogHandler from 'utils/datadog';
import { actionHandlerWithFeatureFlag } from 'utils';
import { FEATURE_FLAGS } from 'utils/constants';
import {
  msApproveRegDocument,
  msGetUploadDocumentList,
  msRejectRegDocument,
  msRevertRegDocument,
  msSendDocumentNotificationToParent,
  msSubmitRegForm,
  msSaveECDAUploadedRegDocument,
  msGetRegistrationDocumentUploadURL,
  msRemoveRegUploadedDocument,
  msSaveUploadedRegDocument,
  msSkipRegDocument,
} from 'services/RegistrationsService';
import {
  SAVE_UPLOADED_REG_DOCUMENTS,
  FETCH_DOCUMENTS_TO_UPLOAD,
  SAVE_ECDA_UPLOADED_REG_DOCUMENTS,
} from '../actionTypes';

import {
  SUBMIT_REG_FORM,
  REVERT_REG_DOCUMENT,
  REMOVE_REG_DOCUMENT,
  APPROVE_REG_DOCUMENT,
  REJECT_REG_DOCUMENT,
  SKIP_REG_DOCUMENT,
  SAVE_REGISTRATION_DOCUMENT,
  SAVE_ECDA_REGISTRATION_DOCUMENT,
  GET_REG_DOCUMENT_UPLOAD_URL,
  GET_REGISTRATION_DOCUMENT_LIST,
  SEND_REG_DOCUMENT_NOTIFICATION,
} from '../../models/documentModelsV2';
import { SkApolloRequest } from '../../../utils/apolloUtils';

const fileName = 'src/redux/actions/Registration/ConsentV2.js';

const dispatchDocumentUploadList = (
  dispatch,
  inProgress = true,
  data = {},
  error = null
) => {
  dispatch({
    type: FETCH_DOCUMENTS_TO_UPLOAD,
    key: 'documentsToUpload',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

const dispatchSaveUploadedDocuments = (
  dispatch,
  inProgress = true,
  data = {},
  error = null
) => {
  dispatch({
    type: SAVE_UPLOADED_REG_DOCUMENTS,
    key: 'savedRegGiroDocuments',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

const dispatchSaveECDAUploadedDocuments = (
  dispatch,
  inProgress = true,
  data = {},
  error = null
) => {
  dispatch({
    type: SAVE_ECDA_UPLOADED_REG_DOCUMENTS,
    key: 'savedECDARegGiroDocuments',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

export const monoGetUploadDocumentList = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const response = await SkApolloRequest({
      params: {
        query: GET_REGISTRATION_DOCUMENT_LIST,
        variables: { IDRegistration },
      },
      type: 'query',
    });
    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoGetUploadDocumentList',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage, success: false };
  }
};

export const getUploadDocumentList = IDRegistration => async dispatch => {
  dispatchDocumentUploadList(dispatch);

  const resp = await actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      dispatch,
      msHandler: msGetUploadDocumentList,
      monoHandler: monoGetUploadDocumentList,
      extraData: { IDRegistration },
    }
  );

  if (resp.success) {
    dispatchDocumentUploadList(dispatch, false, resp.data);
  } else {
    dispatchDocumentUploadList(dispatch, false, null, resp.error);
  }

  return resp;
};

export const monoGetRegistrationDocumentUploadURL = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const file = get(reqData, 'file');
    const response = await SkApolloRequest({
      params: {
        mutation: GET_REG_DOCUMENT_UPLOAD_URL,
        variables: {
          IDRegistration,
          file,
        },
      },
      type: 'mutation',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoGetRegistrationDocumentUploadURL',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const getRegistrationDocumentUploadURL =
  (IDRegistration, file) => async () => {
    return actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        msHandler: msGetRegistrationDocumentUploadURL,
        monoHandler: monoGetRegistrationDocumentUploadURL,
        extraData: { IDRegistration, file },
      }
    );
  };

export const monoSaveECDAUploadedRegDocument = async (_, reqData) => {
  try {
    const { IDECDARegistration, uploads, skipApproval } = reqData;
    return SkApolloRequest({
      params: {
        mutation: SAVE_ECDA_REGISTRATION_DOCUMENT,
        variables: {
          IDECDARegistration,
          documentUploads: uploads,
          skipApproval,
        },
      },
      type: 'mutation',
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoSaveECDAUploadedRegDocument',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const saveECDAUploadedRegDocument =
  (IDECDARegistration, uploads, skipApproval) => async dispatch => {
    dispatchSaveECDAUploadedDocuments(dispatch);

    const resp = await actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        dispatch,
        msHandler: msSaveECDAUploadedRegDocument,
        monoHandler: monoSaveECDAUploadedRegDocument,
        extraData: { IDECDARegistration, uploads, skipApproval },
      }
    );

    if (resp.success) {
      dispatchSaveECDAUploadedDocuments(dispatch, false, resp.data);
    } else {
      dispatchSaveECDAUploadedDocuments(dispatch, false, null, resp.error);
    }

    return resp;
  };

export const monoSaveUploadedRegDocument = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const uploads = get(reqData, 'uploads');
    const response = await SkApolloRequest({
      params: {
        mutation: SAVE_REGISTRATION_DOCUMENT,
        variables: {
          registrationId: IDRegistration,
          documentUploads: uploads,
        },
      },
      type: 'mutation',
    });
    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoSaveUploadedRegDocument',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const saveUploadedRegDocument =
  (IDRegistration, uploads) => async dispatch => {
    dispatchSaveUploadedDocuments(dispatch);
    const resp = await actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        dispatch,
        msHandler: msSaveUploadedRegDocument,
        monoHandler: monoSaveUploadedRegDocument,
        extraData: { IDRegistration, uploads },
      }
    );

    if (resp.success) {
      dispatchSaveUploadedDocuments(dispatch, false, resp.data);
    } else {
      dispatchSaveUploadedDocuments(dispatch, false, null, resp.error);
    }

    return resp;
  };

export const monoRemoveRegUploadedDocument = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const IDDocument = get(reqData, 'IDDocument');
    const remarks = get(reqData, 'remarks');

    const response = await SkApolloRequest({
      params: {
        mutation: REMOVE_REG_DOCUMENT,
        variables: {
          registrationId: IDRegistration,
          documentId: IDDocument,
          remarks,
        },
      },
      type: 'mutation',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoRemoveRegUploadedDocument',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const removeRegUploadedDocument =
  (IDRegistration, IDDocument, remarks) => async () => {
    return actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        msHandler: msRemoveRegUploadedDocument,
        monoHandler: monoRemoveRegUploadedDocument,
        extraData: { IDRegistration, IDDocument, remarks },
      }
    );
  };

export const monoApproveRegDocument = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const IDDocumentType = get(reqData, 'IDDocumentType');
    const remarks = get(reqData, 'remarks');

    const response = await SkApolloRequest({
      params: {
        mutation: APPROVE_REG_DOCUMENT,
        variables: {
          registrationId: IDRegistration,
          documentTypeId: IDDocumentType,
          remarks,
        },
      },
      type: 'mutation',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoApproveRegDocument',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const approveRegDocument =
  (IDRegistration, IDDocumentType, remarks) => async () => {
    return actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        msHandler: msApproveRegDocument,
        monoHandler: monoApproveRegDocument,
        extraData: { IDRegistration, IDDocumentType, remarks },
      }
    );
  };

export const monoRejectRegDocument = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const IDDocumentType = get(reqData, 'IDDocumentType');
    const remarks = get(reqData, 'remarks');

    const response = await SkApolloRequest({
      params: {
        mutation: REJECT_REG_DOCUMENT,
        variables: {
          registrationId: IDRegistration,
          documentTypeId: IDDocumentType,
          remarks,
        },
      },
      type: 'mutation',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoRejectRegDocument',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const rejectRegDocument =
  (IDRegistration, IDDocumentType, remarks) => async () => {
    return actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        msHandler: msRejectRegDocument,
        monoHandler: monoRejectRegDocument,
        extraData: { IDRegistration, IDDocumentType, remarks },
      }
    );
  };

export const monoSkipRegDocument = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const IDDocumentType = get(reqData, 'IDDocumentType');
    const remarks = get(reqData, 'remarks');

    const response = await SkApolloRequest({
      params: {
        mutation: SKIP_REG_DOCUMENT,
        variables: {
          registrationId: IDRegistration,
          documentTypeId: IDDocumentType,
          remarks,
        },
      },
      type: 'mutation',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoSkipRegDocument',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const skipRegDocument =
  (IDRegistration, IDDocumentType, remarks) => async () => {
    return actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        msHandler: msSkipRegDocument,
        monoHandler: monoSkipRegDocument,
        extraData: { IDRegistration, IDDocumentType, remarks },
      }
    );
  };

export const monoRevertRegDocument = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const IDDocumentType = get(reqData, 'IDDocumentType');
    const remarks = get(reqData, 'remarks');

    const response = await SkApolloRequest({
      params: {
        mutation: REVERT_REG_DOCUMENT,
        variables: {
          registrationId: IDRegistration,
          documentTypeId: IDDocumentType,
          remarks,
        },
      },
      type: 'mutation',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoRevertRegDocument',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const revertRegDocument =
  (IDRegistration, IDDocumentType, remarks) => async () => {
    return actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        msHandler: msRevertRegDocument,
        monoHandler: monoRevertRegDocument,
        extraData: { IDRegistration, IDDocumentType, remarks },
      }
    );
  };

export const monoSubmitRegForm = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const response = await SkApolloRequest({
      params: {
        mutation: SUBMIT_REG_FORM,
        variables: { registrationId: IDRegistration },
      },
      type: 'mutation',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoSubmitRegForm',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const submitRegForm = IDRegistration => async () => {
  return actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      msHandler: msSubmitRegForm,
      monoHandler: monoSubmitRegForm,
      extraData: { IDRegistration },
    }
  );
};

export const monoSendDocumentNotificationToParent = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    return await SkApolloRequest({
      params: {
        mutation: SEND_REG_DOCUMENT_NOTIFICATION,
        variables: { IDRegistration },
      },
      type: 'mutation',
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoSendDocumentNotificationToParent',
        fileName,
      },
      'error'
    );
    return { error: ex };
  }
};

export const sendDocumentNotificationToParent = IDRegistration => async () => {
  return actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      msHandler: msSendDocumentNotificationToParent,
      monoHandler: monoSendDocumentNotificationToParent,
      extraData: { IDRegistration },
    }
  );
};
